import {Button} from '@wandb/weave/components';
import * as Dialog from '@wandb/weave/components/Dialog';
import {Icon} from '@wandb/weave/components/Icon';
import {ApolloQueryResult} from 'apollo-client';
import React from 'react';
import {toast} from 'react-toastify';

import {
  useDeleteInviteMutation,
  useDeleteOAuthClientMutation,
} from '../../generated/graphql';
import {TeamPageQueryData} from '../../pages/Team/types';
import {
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../../util/errors';
import * as S from '../TeamSettings/TeamSettings.styles';

type DeleteServiceAccountModalProps = {
  isOpen: boolean;
  close: () => void;
  id: string;
  name: string;
  clientId?: string;
  entityName: string;
  refetch?: () => Promise<ApolloQueryResult<TeamPageQueryData>>;
};

export const DeleteServiceAccountModal = ({
  isOpen,
  close,
  id,
  name,
  clientId,
  entityName,
  refetch,
}: DeleteServiceAccountModalProps) => {
  const [deleteInviteMutation] = useDeleteInviteMutation({
    context: propagateErrorsContext(),
  });
  const [deleteOAuthClientMutation] = useDeleteOAuthClientMutation({
    context: propagateErrorsContext(),
  });

  const deleteServiceAccount = async () => {
    try {
      await deleteInviteMutation({
        variables: {
          id,
          entityName,
        },
      });
      if (refetch != null) {
        refetch();
      }
      toast.success('Service account deleted');
      close();
    } catch (err) {
      toast.error(extractErrorMessageFromApolloError(err));
    }
  };

  const deleteOAuthClient = async () => {
    if (!clientId) {
      return;
    }
    try {
      await deleteOAuthClientMutation({
        variables: {
          clientId,
          entityName,
        },
      });
      refetch?.();
      toast.success('Service account deleted');
      close();
    } catch (err) {
      toast.error(extractErrorMessageFromApolloError(err));
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={close}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content>
          <Dialog.Title>
            <S.AlignedSection>
              <Icon name="warning-alt" className="mr-10" />
              Delete {name}?
            </S.AlignedSection>
          </Dialog.Title>
          <Dialog.Description>
            <p>
              Are you sure you want to delete this service account? This will
              break any existing workflows using this service account.
            </p>
          </Dialog.Description>
          <div className="mt-24 flex justify-end gap-8">
            <Dialog.Close>
              <Button
                onClick={clientId ? deleteOAuthClient : deleteServiceAccount}
                variant="destructive">
                Confirm
              </Button>
            </Dialog.Close>
            <Dialog.Close>
              <Button onClick={close} variant="ghost">
                Cancel
              </Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
